import * as Dialog from "@radix-ui/react-dialog";
import { useDemoMode } from "../../lib/useApi.ts";
import {
  ArrowRight,
  ArrowLeft,
  AudioLines,
  Bell,
  CalendarDays,
  ChartNoAxesCombined,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock3,
  CloudLightning,
  CreditCard,
  Download,
  Ellipsis,
  ExternalLink,
  Eye,
  FileText,
  Flag,
  Fullscreen,
  GitBranch,
  Headphones,
  Inbox,
  Info,
  Link2,
  ListFilter,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Menu,
  MessageCircle,
  Mic,
  Moon,
  Network,
  Pause,
  Phone,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Trash2,
  User,
  Users,
  Volume2,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import type { Tone } from "../lib/model";
const icons: Record<string, LucideIcon> = {
  arrow: ArrowRight,
  back: ArrowLeft,
  audio: AudioLines,
  bell: Bell,
  calendar: CalendarDays,
  chart: ChartNoAxesCombined,
  check: Check,
  doublecheck: CheckCheck,
  success: CheckCircle2,
  down: ChevronDown,
  chevron: ChevronRight,
  help: CircleHelp,
  clock: Clock3,
  storm: CloudLightning,
  billing: CreditCard,
  download: Download,
  more: Ellipsis,
  external: ExternalLink,
  eye: Eye,
  file: FileText,
  flag: Flag,
  fullscreen: Fullscreen,
  flow: GitBranch,
  headphones: Headphones,
  inbox: Inbox,
  info: Info,
  link: Link2,
  filter: ListFilter,
  loader: LoaderCircle,
  lock: LockKeyhole,
  mail: Mail,
  menu: Menu,
  message: MessageCircle,
  mic: Mic,
  moon: Moon,
  network: Network,
  pause: Pause,
  phone: Phone,
  play: Play,
  plus: Plus,
  radio: Radio,
  refresh: RefreshCw,
  search: Search,
  settings: Settings2,
  shield: ShieldCheck,
  sliders: SlidersHorizontal,
  sun: Sun,
  trash: Trash2,
  user: User,
  users: Users,
  volume: Volume2,
  close: X,
};
export function Icon({
  name,
  size = 20,
  className = "",
}: {
  name: string;
  size?: number;
  className?: string;
}) {
  const C = icons[name] || CircleHelp;
  return (
    <C size={size} strokeWidth={1.7} aria-hidden="true" className={className} />
  );
}
export function Button({
  children,
  variant = "default",
  icon,
  loading,
  className = "",
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "ghost" | "danger";
  icon?: string;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      {...props}
      disabled={disabled || loading}
      className={`button button-${variant} ${className}`}
      aria-busy={loading || undefined}
    >
      {loading ? (
        <Icon name="loader" className="spin" />
      ) : icon ? (
        <Icon name={icon} />
      ) : null}
      {children}
    </button>
  );
}
export function Badge({
  children,
  tone = "neutral",
  dot = false,
}: {
  children: ReactNode;
  tone?: Tone;
  dot?: boolean;
}) {
  return (
    <span className={`badge badge-${tone}`}>
      {dot && <span className="status-dot" />}
      {children}
    </span>
  );
}
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        {eyebrow && <span className="eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {description && <p className="muted">{description}</p>}
      </div>
      {actions && <div className="button-row">{actions}</div>}
    </div>
  );
}
export function Panel({
  title,
  action,
  children,
  className = "",
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`panel ${className}`}>
      {(title || action) && (
        <div className="panel-heading">
          <h2>{title}</h2>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}
export function EmptyState({
  title = "Nothing here yet",
  text,
  icon = "inbox",
  action,
}: {
  title?: string;
  text?: string;
  icon?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="icon-well">
        <Icon name={icon} size={28} />
      </span>
      <h2>{title}</h2>
      {text && <p className="muted">{text}</p>}
      {action}
    </div>
  );
}
export function Notice({
  children,
  tone = "blue",
}: {
  children: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className={`notice notice-${tone}`}>
      <Icon name={tone === "red" ? "bell" : "info"} />
      <div>{children}</div>
    </div>
  );
}
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  sheet = false,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  sheet?: boolean;
  wide?: boolean;
}) {
  const opener = useRef<HTMLElement | null>(null);
  // The tag was unconditional, so every dialog on a LIVE workspace claimed its actions were
  // simulated. Only say it when it is true.
  const { demo } = useDemoMode();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content
          className={`dialog-content ${sheet ? "is-sheet" : ""} ${wide ? "is-wide" : ""}`}
          onOpenAutoFocus={() => {
            opener.current = document.activeElement as HTMLElement | null;
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const target = opener.current?.isConnected
              ? opener.current
              : document.getElementById("main-content");
            target?.focus();
          }}
        >
          <div className="dialog-head">
            <div>
              {demo && <div className="dialog-demo">Demo · actions simulated</div>}
              <Dialog.Title>{title}</Dialog.Title>
              <Dialog.Description className={description ? "muted" : "sr-only"}>
                {description || `${title} controls`}
              </Dialog.Description>
            </div>
            <Dialog.Close className="icon-button" aria-label="Close dialog">
              <Icon name="close" />
            </Dialog.Close>
          </div>
          <div className="dialog-body">{children}</div>
          {footer && <div className="dialog-footer">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
export function Toggle({
  label,
  checked,
  onChange,
  disabled,
  description,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  description?: string;
}) {
  return (
    <label className="toggle-row">
      <span>
        <strong>{label}</strong>
        {description && <small className="muted">{description}</small>}
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="toggle-track" aria-hidden="true" />
    </label>
  );
}
export function Tabs({
  items,
  value,
  onChange,
}: {
  items: { label: string; value: string; count?: number }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="tab-strip" role="group" aria-label="Filter view">
      {items.map((x) => (
        <button
          key={x.value}
          aria-pressed={x.value === value}
          onClick={() => onChange(x.value)}
          className={x.value === value ? "selected" : ""}
        >
          {x.label}
          {x.count !== undefined && <span className="count">{x.count}</span>}
        </button>
      ))}
    </div>
  );
}
export function Stat({
  value,
  label,
  to,
}: {
  value: ReactNode;
  label: string;
  to: string;
}) {
  return (
    <Link className="stat" to={to}>
      <strong>{value}</strong>
      <span>
        {label}
        <Icon name="chevron" size={15} />
      </span>
    </Link>
  );
}
export function AudioPreview({
  text,
  compact = false,
}: {
  text: string;
  compact?: boolean;
}) {
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState("");
  useEffect(
    () => () => {
      if (typeof window !== "undefined" && "speechSynthesis" in window)
        window.speechSynthesis.cancel();
    },
    [],
  );
  function play() {
    if (!("speechSynthesis" in window)) {
      setError(
        "Device speech preview is unavailable. The message is shown in full.",
      );
      return;
    }
    if (playing) {
      speechSynthesis.cancel();
      setPlaying(false);
      return;
    }
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.onend = () => setPlaying(false);
    utterance.onerror = () => {
      setPlaying(false);
      setError("Could not play the device preview.");
    };
    speechSynthesis.cancel();
    speechSynthesis.speak(utterance);
    setPlaying(true);
  }
  return (
    <div className="audio-preview">
      <button
        className="play-button"
        aria-label={
          playing ? "Stop device voice preview" : "Play device voice preview"
        }
        onClick={play}
      >
        <Icon name={playing ? "pause" : "play"} size={compact ? 21 : 25} />
      </button>
      {!compact && (
        <>
          <div
            className={`waveform ${playing ? "is-playing" : ""}`}
            aria-hidden="true"
          >
            {Array.from({ length: 25 }, (_, i) => (
              <i
                key={i}
                style={{
                  height: `${8 + ((i * 17) % 31)}px`,
                  animationDelay: `${i * 0.03}s`,
                }}
              />
            ))}
          </div>
          <span className="sr-only">
            Uses your device voice, not the configured phone voice.
          </span>
        </>
      )}
      {error && <small role="status">{error}</small>}
    </div>
  );
}
